import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Bell, ChevronDown, LogOut, LayoutDashboard, User } from 'lucide-react';
import config from '../../config';
import { setAuthNext } from '../../lib/bookingCart';
import { brandLogo, useSiteContent } from '../../lib/siteContent';

function readCustomerUser() {
  try {
    return JSON.parse(localStorage.getItem('customer_user') || '{}');
  } catch {
    return {};
  }
}

export default function Navbar({ accountMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const site = useSiteContent();
  const navbarTagline = site.navbar?.tagline || 'Uncompromising Care. Seamlessly Yours.';
  const logoSrc = brandLogo(site);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [user, setUser] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const refreshAuth = () => {
    setSignedIn(Boolean(localStorage.getItem('customer_token')));
    setUser(readCustomerUser());
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    refreshAuth();
    const onStorage = () => refreshAuth();
    window.addEventListener('storage', onStorage);
    window.addEventListener('customer-auth-changed', onStorage);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('customer-auth-changed', onStorage);
    };
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const logout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_user');
    localStorage.removeItem('customer_role');
    window.dispatchEvent(new Event('customer-auth-changed'));
    setMenuOpen(false);
    navigate('/');
  };

  const rawEmail = (user.email || '').trim();
  const isPhoneEmail = rawEmail.endsWith('@phone.bookmyjab.local');
  const phoneDigits = String(user.phone || '').replace(/\D/g, '');
  const displayName = (user.full_name || '').trim()
    || (phoneDigits.length >= 10 ? `+91 ${phoneDigits.slice(-10).slice(0, 5)} ${phoneDigits.slice(-10).slice(5)}` : '')
    || (!isPhoneEmail && rawEmail ? rawEmail : '')
    || 'Account';
  const initials = (user.full_name || '').trim()
    ? String(user.full_name)
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join('')
    : (phoneDigits.slice(-2) || 'U');

  const navLinks = [
    { label: 'Home', href: '/', route: true },
    { label: 'About', href: '/about', route: true },
    { label: 'Vaccines & Care', href: '/vaccination-catalogue', route: true },
    { label: 'Health Library', href: '/articles', route: true },
  ];

  const scrollToSection = (href) => {
    setMobileOpen(false);
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      data-testid="public-navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled || accountMode
          ? 'bg-white/95 backdrop-blur-xl shadow-sm border-b border-[#673c96]/10'
          : 'bg-white/80 backdrop-blur-sm'
        }`}
      style={{ ["--bmj-nav-h"]: scrolled || accountMode ? "76px" : "88px" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between min-h-[76px] md:min-h-[88px] py-2">
          <Link
            to="/"
            className="flex flex-col items-start justify-center shrink-0"
            data-testid="navbar-logo"
            style={{ minWidth: '170px' }}
            onClick={(e) => {
              if (window.location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <img
              src={logoSrc}
              alt="BookMyJab"
              style={{
                width: '160px',
                minWidth: '140px',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
            <span
              data-testid="navbar-tagline"
              className="text-[9px] md:text-[10px] italic font-medium text-[#311b52]/70 tracking-[0.12em] uppercase leading-tight mt-0.5"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {navbarTagline}
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7" data-testid="desktop-nav">
            {navLinks.map((link) => (
              link.route ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-sm font-medium text-[#3e195e] hover:text-[#f37721] transition-colors tracking-wide whitespace-nowrap"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  onClick={() => scrollToSection(link.href)}
                  className="text-sm font-medium text-[#3e195e] hover:text-[#f37721] transition-colors tracking-wide whitespace-nowrap"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {link.label}
                </button>
              )
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3 shrink-0">
            {signedIn ? (
              <div className="relative flex items-center gap-2" ref={menuRef}>
                <Link
                  to="/vaccination-catalogue"
                  className="bg-[#f37721] text-white hover:bg-[#d9651a] rounded-full px-5 py-2 text-sm font-semibold whitespace-nowrap"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Book now
                </Link>
                <Link
                  to="/dashboard"
                  className="p-2 rounded-full text-[#673c96] hover:bg-[#673c96]/8 transition-colors"
                  aria-label="Notifications"
                >
                  <Bell className="w-5 h-5" />
                </Link>
                <button
                  type="button"
                  onClick={() => setMenuOpen((o) => !o)}
                  className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-[#f5f3f7] transition-colors"
                  data-testid="navbar-user-menu"
                >
                  <span
                    className="text-sm font-medium text-[#311b52] max-w-[140px] truncate"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {displayName}
                  </span>
                  <span className="w-9 h-9 rounded-full bg-[#673c96]/15 text-[#673c96] flex items-center justify-center text-xs font-semibold">
                    {initials}
                  </span>
                  <ChevronDown className="w-4 h-4 text-[#673c96]" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-lg border border-[#673c96]/10 py-2 z-50">
                    <Link
                      to="/dashboard"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#311b52] hover:bg-[#f5f3f7]"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      <LayoutDashboard className="w-4 h-4 text-[#673c96]" /> My account
                    </Link>
                    <Link
                      to="/profile"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#311b52] hover:bg-[#f5f3f7]"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      <User className="w-4 h-4 text-[#673c96]" /> Profile
                    </Link>
                    <button
                      type="button"
                      onClick={logout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#ee4036] hover:bg-red-50"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => { if (location.pathname.startsWith('/booking')) setAuthNext('/booking'); }}
                  className="text-sm font-medium text-[#311b52] hover:text-[#f37721]"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Sign in
                </Link>
                <Link
                  to="/vaccination-catalogue"
                  data-testid="navbar-request-btn"
                  className="bg-[#f37721] text-white hover:bg-[#d9651a] shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 rounded-full px-7 py-2.5 text-sm font-semibold whitespace-nowrap"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Book now
                </Link>
              </>
            )}
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-[#311b52]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-[#673c96]/10 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              link.route ? (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block w-full text-left px-4 py-3 text-sm font-medium text-[#3e195e] hover:bg-[#f5f3f7] rounded-lg"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  onClick={() => scrollToSection(link.href)}
                  className="block w-full text-left px-4 py-3 text-sm font-medium text-[#3e195e] hover:bg-[#f5f3f7] rounded-lg"
                >
                  {link.label}
                </button>
              )
            ))}
            {signedIn ? (
              <>
                <Link
                  to="/vaccination-catalogue"
                  className="block w-full text-center mt-3 bg-[#f37721] text-white rounded-full px-6 py-3 text-sm font-semibold"
                  onClick={() => setMobileOpen(false)}
                >
                  Book now
                </Link>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  className="block w-full text-left px-4 py-3 text-sm font-medium text-[#311b52]"
                >
                  My account
                </Link>
                <button
                  type="button"
                  onClick={() => { setMobileOpen(false); logout(); }}
                  className="block w-full text-left px-4 py-3 text-sm font-medium text-[#ee4036]"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => {
                    if (location.pathname.startsWith('/booking')) setAuthNext('/booking');
                    setMobileOpen(false);
                  }}
                  className="block w-full text-left px-4 py-3 text-sm font-medium text-[#311b52]"
                >
                  Sign in
                </Link>
                <Link
                  to="/vaccination-catalogue"
                  className="block w-full text-center mt-3 bg-[#f37721] text-white rounded-full px-6 py-3 text-sm font-semibold"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-nav-request-btn"
                >
                  Book now
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
